import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Download, Upload, X, AlertTriangle } from "lucide-react";
import { handleAuthError } from "../../utils/functions";

const API = `${process.env.REACT_APP_BASE_URL}/api/grant-import`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const GrantImportButtons = ({ importType, label, onImported, canDownloadSample = true, canImport = true }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadSample = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/${importType}/sample`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${importType}-grants-import-sample.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      if (!handleAuthError(error, dispatch, navigate)) toast.error("Could not download the sample file");
    } finally {
      setDownloading(false);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      toast.error("Please choose the filled Excel sample (.xlsx) or a .csv file.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/${importType}`, formData, { headers: authHeader() });
      toast.success(res.data?.message || "Grants imported successfully");
      setResult(null);
      if (onImported) onImported();
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      const data = error?.response?.data;
      if (data?.errors?.length) {
        setResult({ message: data.message, errors: data.errors });
      } else {
        toast.error(data?.message || "Import failed");
      }
    } finally {
      setImporting(false);
    }
  };

  const buttonClass =
    "flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px] text-white disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <>
      {canDownloadSample && (
      <button
        type="button"
        onClick={downloadSample}
        disabled={downloading}
        className={`${buttonClass} bg-[#137e81] hover:bg-[#0f6b6e]`}
        title={`Download an Excel sample with all ${label} columns`}
      >
        <Download size={15} />
        {downloading ? "Downloading..." : "Download Excel Sample"}
      </button>
      )}
      {canImport && (
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={importing}
        className={`${buttonClass} bg-[#137e81] hover:bg-[#0f6b6e]`}
        title="Import grants from the filled Excel sample"
      >
        <Upload size={15} />
        {importing ? "Importing..." : "Import Excel"}
      </button>
      )}
      {canImport && (
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        className="hidden"
        onChange={(e) => {
          importFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      )}

      {result && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-[#e5e7eb] px-5 py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 flex-shrink-0 text-[#dc2626]" size={20} />
                <div>
                  <h2 className="text-base font-semibold text-[#111827]">{label} import failed</h2>
                  <p className="mt-1 text-sm text-[#4b5563]">{result.message}</p>
                </div>
              </div>
              <button type="button" onClick={() => setResult(null)} className="text-[#6b7280] hover:text-[#111827]" title="Close">
                <X size={20} />
              </button>
            </div>
            <div className="overflow-auto px-5 py-3">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-[#374151]">
                    <th className="w-16 border-b border-[#e5e7eb] py-2 pr-3 font-semibold">Row</th>
                    <th className="border-b border-[#e5e7eb] py-2 pr-3 font-semibold">Column</th>
                    <th className="border-b border-[#e5e7eb] py-2 font-semibold">Problem</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((e, i) => (
                    <tr key={i} className="align-top text-[#374151]">
                      <td className="border-b border-[#f3f4f6] py-1.5 pr-3">{e.row}</td>
                      <td className="border-b border-[#f3f4f6] py-1.5 pr-3 font-medium">{e.column || "-"}</td>
                      <td className="border-b border-[#f3f4f6] py-1.5">{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-[#e5e7eb] px-5 py-3">
              <p className="text-xs text-[#6b7280]">Row numbers match the rows in Excel (row 1 is the header).</p>
              <button
                type="button"
                onClick={() => setResult(null)}
                className="rounded bg-[#137e81] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#0f6b6e]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GrantImportButtons;
